import Model, { attr } from '@ember-data/model';

import type {
	DataSourceStatus,
	SelectSourceOptionType,
	SourceStatusLevel as StatusLevel,
} from 'webapp/enums/enums';
import type { VerificationInfo } from 'webapp/utils/verification';

import type { StatusObjectAction, StatusObjectType } from 'webapp/models-custom/status-object';

export interface Connection {
	id: number;
	name: string;
	numberOfSources: number;
	canReconnect: boolean; // Checking if DS is created in same space as users current space or user is root space level admin
	userId: number | null; // Not used
	sharableStatus: ConnectionSharableStatus | null;
	permissionStatus: ConnectionPermissionStatus | null;
}

export interface DataSourceParentInfo {
	spaceAccessId: number;
	title: string;
}

export interface DataSourceSyncInfo {
	requestId?: string;
	startedAt: number;
	finishedAt?: number;
	syncType: DataSourceSyncType;
	syncStatus: DataSourceSyncStatus;
	lastSuccessfulSyncAt?: number;
	dataRangeFrom?: number;
	dataRangeTo?: number;
	dataAvailableAt?: number;
}

export interface DataSourceDatasetsMetadata {
	entity: string;
	columnsCount: number;
}

export enum ConnectionSharableStatus {
	SPACE = 0,
	CLIENTS = 1,
}

export enum ConnectionPermissionStatus {
	TEAMMATE = 0,
	PRIVATE = 1,
	SELECTED_USERS = 2,
	EVERYONE = 3,
}

export enum DataSourceSyncType {
	INITIAL_SYNC = 1,
	USER_REQUEST = 2,
	MODIFICATION_REQUEST = 3,
	SCHEDULED_UPDATE = 4,
}

export enum DataSourceSyncStatus {
	SUCCESS = 1,
	FAILED = 2,
	IN_PROGRESS = 3,
	PARTIAL_SYNC = 4,
	SUCCESS_WITH_WARNINGS = 5,
	CANCELED = 6,
}

export default class SourceRecord extends Model {
	@attr('number') type!: number;
	@attr('object') connection?: Connection;
	@attr('string') title!: string;
	@attr('string', { defaultValue: '' }) subtitle!: string;
	@attr('string') avatar!: string;
	@attr('string') wrapperFor!: string | null;
	@attr('string') statusLevel!: StatusLevel | ''; // '' when BE sends none (SourceSerializer.normalize)
	@attr('string') statusTitle!: string;
	@attr('string') statusDescription!: string;
	@attr('string') statusAction!: StatusObjectAction | '' | null;
	@attr('string') statusType!: StatusObjectType | '' | null;
	@attr('string') statusUserResponseLink!: string;
	@attr('number') created!: number;
	@attr('number') createdById!: number;
	@attr('number') ownerId!: number; // currently ownerId is same as createdById
	@attr('number') sourceId!: number; // data_source_id
	@attr('string') token!: string;
	@attr('boolean') isRoot!: boolean;
	@attr('boolean') isLocked!: boolean;
	@attr('boolean') isTypeIngest!: boolean;
	@attr('string') timezone!: string;
	@attr('string') initialTitle!: string;
	@attr('string') selectedSourceOption!: SelectSourceOptionType;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	users!: number[];
	@attr('number') customSyncInterval!: number;
	@attr('object') parentInfo?: DataSourceParentInfo;
	@attr('object') lastSync?: DataSourceSyncInfo;
	@attr('object') status!: DataSourceStatus;
	@attr('object') datasetsMetadata?: DataSourceDatasetsMetadata;
	@attr('object') verificationInfo?: VerificationInfo;
}
